import { APIError } from "@certn/api";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  SectionMessage,
  Space,
  SummaryCard,
  showToast,
  useColors,
} from "@certn/cascadia-react";
import { getTextStyle } from "@certn/cascadia-react/foundations/typography/styles";
import {
  CascadiaJsonFormContext,
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "@certn/forms";
import { RenderError } from "@certn/forms/src/components/helpers/RenderError";
import { Text } from "@certn/forms/src/components/Text";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { BaseLayoutContent } from "@certn/forms/src/layouts/BaseLayoutContent";
import { hasLabel } from "@certn/forms/src/utils";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useJsonForms } from "@jsonforms/react";
import * as Sentry from "@sentry/react";
import { useCallback, useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useApplication } from "../ApplicationContext";
import { useApplicationFormContext } from "../ApplicationFormContext";
import { ConsentRequiredModal } from "../controls/consent-control/ConsentRequiredModal";
import { useConsentStepValidation } from "../utils";
import { QuickscreenConsentsContent } from "./QuickscreenConsentsContent";
import { SummaryEditLink } from "./SummaryEditLink";

export function QuickscreenConsentsLayout(props: CascadiaLayoutProps) {
  const { t } = useTranslation();
  const colors = useColors();
  const { isSummary, isSubmitting, submit, readonly } = useContext(
    CascadiaJsonFormContext
  );
  const { prevSection, navigateToSection, partiallySaveData, basePath } =
    useApplication();
  const { caseId } = useApplicationFormContext();
  const jsonFormsCtx = useJsonForms();

  const { uischema } = props;
  const { showValidationModal, setShowValidationModal, handleNextStep } =
    useConsentStepValidation(
      props.schema,
      uischema,
      submit,
      jsonFormsCtx.core?.data ?? {}
    );

  // Hide the "I confirm..." JsonForms checkbox when there are zero consents to
  // show — the claim value is still auto-set to true so submit validation passes.
  const [hideAcknowledgement, setHideAcknowledgement] = useState(false);

  const handleEmptyConsentsChange = useCallback(
    (isEmpty: boolean) => setHideAcknowledgement(isEmpty),
    []
  );

  const options = uischema.options || {};

  if (!hasLabel(uischema)) {
    return <RenderError>{t("Invalid Category")}</RenderError>;
  }

  if (isSummary) {
    const typedUischema = uischema as typeof uischema & {
      label?: string;
      name?: string;
    };
    const sectionName = typedUischema.name;
    return (
      <SummaryCard
        header={typedUischema.label}
        testID="quickscreen-consents-layout-summary"
        actions={
          !readonly &&
          sectionName && (
            <SummaryEditLink
              href={`/${basePath ?? "quickscreen"}/${caseId}/${slugify(sectionName)}`}
              testID="quickscreen-consents-summary-edit-button"
            />
          )
        }
      >
        <BaseLayout {...props}>
          <BaseLayoutContent />
        </BaseLayout>
      </SummaryCard>
    );
  }

  return (
    <>
      <BaseLayout
        testID="quickscreen-consents-layout"
        {...props}
        className={styles.layout}
      >
        <div className={styles.heading}>
          <h1
            className={styles.label}
            data-testid={`${slugify(props.path)}-title`}
          >
            {uischema.label}
          </h1>
          {options.subLabel && (
            <Text
              style="form/body-primary"
              color="text-weak"
              data-testid={`${slugify(props.path)}-sub-title`}
            >
              {options.subLabel}
            </Text>
          )}
          {options.sectionMessage && (
            <SectionMessage
              type={options.sectionMessage.type}
              sectionTitle={options.sectionMessage.heading}
              icon={options.sectionMessage.type}
              testID="category-section-message"
            >
              {options.sectionMessage.content}
            </SectionMessage>
          )}
        </div>

        <div className={styles.subHeading}>
          <Text style="form/section-heading">
            {t("Region-specific notices")}
          </Text>
          <Text style="form/body-primary" color="text-weak">
            {t(
              "The following notices apply based on the applicant's location."
            )}
          </Text>
        </div>

        <QuickscreenConsentsContent
          caseId={caseId}
          acceptedPath="is_quickscreen_regional_consents_accepted"
          onEmptyConsentsChange={handleEmptyConsentsChange}
        />

        {!hideAcknowledgement && <BaseLayoutContent />}
      </BaseLayout>

      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid ${colors["border-base"]}`,
        })}
      >
        <ButtonGroup justify="end">
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          <PrimaryButton
            onPress={handleNextStep}
            variant="brand-primary"
            isLoading={isSubmitting}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>

      {/* TODO: Review verbiage with design — copy may need updating per consent type */}
      <ConsentRequiredModal
        show={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        alternativeDialogContent={{
          header: t("Notice acknowledgement required"),
          content: (
            <>
              <Trans>
                You can't continue yet. You must confirm that the applicant has
                received and reviewed all required notices.
              </Trans>
              <br />
              <br />
              <Trans>Select the checkbox on this page to proceed.</Trans>
            </>
          ),
        }}
      />
    </>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  label: css(getTextStyle("form/page-title"), { margin: 0 }),
  subHeading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
};

QuickscreenConsentsLayout.displayName = "QuickscreenConsentsLayout";

export const QuickscreenConsentsLayoutRenderer = withCascadiaLayoutProps(
  QuickscreenConsentsLayout
);
